import React from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import emptyFunction from 'common/util/emptyFunction';
import { formatPrice } from 'common/formatters';
import { calculatePayoutAmount } from 'common/utility/financeHelpers';
import { UTILITY_TYPE_ID_INTERNET } from 'common/utility/constants';

import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrDarkModal from 'client/components/SrDarkModal';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrModalContent from 'client/components/SrModalContent';
import SrFlex from 'client/components/SrFlex';

const getFormValues = ({ utilityTypes, values = {}, setFieldValue = emptyFunction }) => {
  const { activation } = values;
  const activationItemUtilityTypeIds =
    activation?.items?.map(({ utilityTypeId }) => utilityTypeId) || [];

  const availableUtilityTypes = (utilityTypes || []).filter(({ id }) => {
    return id !== UTILITY_TYPE_ID_INTERNET && activationItemUtilityTypeIds.includes(id);
  });

  return [
    {
      id: 'activation',
      type: 'hidden',
    },
    {
      id: 'activationAutoComplete',
      title: 'Activation',
      type: 'activation_auto_complete',
      properties: {
        placeholder: 'Search by address, user name, or email',
        onSuggestionSelected: (suggestion) => {
          setFieldValue('activation', suggestion);
        },
      },
      validations: ['required'],
    },
    hideFieldIf(!activation, {
      id: 'utilityTypeId',
      title: 'Choose a Utility Type',
      type: 'info_checkbox',
      properties: {
        choices: availableUtilityTypes.map((utilityType) => ({
          value: utilityType.id,
          label: (
            <SrFlex align="center" columnGap="5px">
              <SrBox marginLeft="5px">
                <SrText
                  weight="800"
                  fontSize="16px"
                  font="greycliff"
                  lineHeight="25px"
                  display="block"
                >
                  {utilityType.label}
                </SrText>
              </SrBox>
              <SrBox
                width="20px"
                height="20px"
                backgroundImage={`url("${utilityType.iconUrl}")`}
                backgroundSize="cover"
                marginRight="5px"
              />
            </SrFlex>
          ),
        })),
      },
      validations: ['required'],
    }),
    {
      // FEAT-D26: the External Invoice ID (PID) is intentionally NOT required.
      // Ops can create a non-internet invoice and mark it paid without a PID and
      // capture it later, so this field stays available but no longer blocks save.
      id: 'externalAccountNumberId',
      title: 'External Invoice ID',
      type: 'short_text',
      properties: {
        placeholder: 'Enter external account number',
      },
    },
    {
      id: 'commissionAmount',
      title: 'Commission Amount',
      type: 'currency',
      properties: {
        placeholder: 'Enter commission amount',
        threshold: 1,
      },
      validations: ['required', 'greaterOrEqualTo'],
    },
  ];
};

const UPAdminAccountingNonInternetAddInvoiceModal = ({ utilityTypes, onSave, onCancel }) => {
  return (
    <>
      <SrDarkModal onClose={onCancel} className="UPAdminAccountingNonInternetAddInvoiceModal">
        <SrModalContent style={{ width: 412, marginTop: 4 }}>
          <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            <SrText
              weight="800"
              font="greycliff"
              fontSize="25px"
              lineHeight="30px"
              letterSpacing="0.5"
            >
              Add Non-Internet Invoice
            </SrText>
          </SrBox>
          <SrSeparator marginTop="20px" marginBottom="8px" />
          <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            <Formik
              initialValues={createInitialValues({
                formFields: getFormValues({
                  utilityTypes,
                }),
              })}
              validate={(formValues) => {
                return createFormValidationFn(
                  getFormValues({
                    utilityTypes,
                    values: formValues,
                  }),
                )(formValues);
              }}
              onSubmit={onSave}
              render={(formikBag) => {
                const formFields = getFormValues({
                  utilityTypes,
                  values: formikBag.values,
                  setFieldValue: formikBag.setFieldValue,
                  formikBag,
                });

                const commissionAmount = formikBag?.values?.commissionAmount || 0;
                const payoutAmount = calculatePayoutAmount({ commissionAmount });

                return (
                  <Form style={{ width: '100%', maxWidth: '350px' }}>
                    <SrBox margin="22px 0">
                      {formFields.map((formField) => {
                        return (
                          <>
                            <SrFormikField
                              key={formField.id}
                              formField={formField}
                              formikBag={formikBag}
                              className={SR_FIELD_THEME_ADOBE}
                            />
                            {formField.id === 'commissionAmount' && (
                              <SrText
                                display="block"
                                lineHeight="25px"
                                font="greycliff"
                                color={payoutAmount > 0 ? 'green' : 'orange300'}
                                fontSize="14px"
                                weight="500"
                                align="center"
                                paddingTop="8px"
                              >
                                {payoutAmount > 0 ? (
                                  <>
                                    The partner will receive a payout of&nbsp;
                                    <SrText
                                      weight="800"
                                      fontSize="14px"
                                      align="center"
                                      color="green"
                                      paddingTop="8px"
                                    >
                                      {formatPrice(payoutAmount)}
                                    </SrText>
                                    &nbsp;for this invoice.
                                  </>
                                ) : (
                                  'The partner will not receive a payout for this invoice, ' +
                                  'the commission amount is too low.'
                                )}
                              </SrText>
                            )}
                          </>
                        );
                      })}
                    </SrBox>
                    <SrBox className="submitButtonContainer">
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        disabled={!formikBag.isValid}
                        hasBoxShadow
                      >
                        Save
                      </SrButton>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREY}
                        type="button"
                        margin="12px 0px"
                        onClick={onCancel}
                        hasBoxShadow
                      >
                        Cancel
                      </SrButton>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
    </>
  );
};

UPAdminAccountingNonInternetAddInvoiceModal.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

UPAdminAccountingNonInternetAddInvoiceModal.displayName =
  'UPAdminAccountingNonInternetAddInvoiceModal';

export default UPAdminAccountingNonInternetAddInvoiceModal;
